import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";

const SUBMIT_URL = "https://readdy.ai/api/form/db6f70bghk82orf0cksg";

const values = [
  { icon: "ri-camera-3-line", title: "专业收录", desc: "与摄影师、模特合作，持续收录高质量写真作品。" },
  { icon: "ri-layout-grid-line", title: "多维分类", desc: "按风格、场景、模特与年份组织，快速定位作品。" },
  { icon: "ri-heart-3-line", title: "本地收藏", desc: "无需登录即可收藏心仪作品，随时回看。" },
];

const faqs = [
  {
    q: "国模白露的作品可以商用吗？",
    a: "站内作品仅用于作品展示与欣赏。如需商业使用，请通过下方反馈表单联系我们取得授权。",
  },
  {
    q: "收藏的作品会保留多久？",
    a: "收藏数据保存在你当前使用的浏览器本地，只要不清理浏览器数据，收藏就会一直保留。",
  },
  {
    q: "如何联系到摄影师或模特？",
    a: "请通过反馈表单说明来意，我们会在核实后协助转达相关合作信息。",
  },
  {
    q: "作品会持续更新吗？",
    a: "会。我们会不定期上线新的写真作品，欢迎收藏本站或关注我们的社交账号。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    formData.delete("website_alt");
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交内容被识别为垃圾信息，请修改后重试。"
            : typeof serverMsg === "string" && serverMsg
              ? serverMsg
              : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="pb-20 pt-24 md:pb-28 md:pt-28">
      <div className="px-4 md:px-8">
        <div className="mb-6">
          <Breadcrumb items={[{ label: "关于与联系" }]} />
        </div>
        <div className="max-w-3xl">
          <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-[0.3em] text-primary-600">
            <span className="h-px w-6 bg-primary-500" />
            ABOUT US
          </p>
          <h1 className="text-3xl font-bold text-foreground-950 md:text-4xl">关于国模白露</h1>
          <p className="mt-4 text-sm leading-loose text-foreground-600 md:text-base">
            国模白露是一个专注于高质量模特写真摄影作品展示与欣赏的图库平台。我们相信，好的写真不止是记录美，
            更是摄影师、模特与光影共同完成的创作。平台面向摄影爱好者、模特粉丝以及写真行业从业者，
            提供清晰的分类浏览、多维筛选与收藏体验，让每一次欣赏都足够从容。
          </p>
        </div>
      </div>

      <section className="mt-12 px-4 md:px-8">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-700">
                <i className={item.icon} />
              </span>
              <h2 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-16 bg-background-100 px-4 py-16 md:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold text-foreground-950">运营团队</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-600">
              国模白露由一支热爱人像与时尚摄影的小团队运营，成员包括内容编辑、合作摄影师与影像后期。
              我们坚持人工筛选与整理每一组作品，力求呈现真实、优质的写真内容，也希望为摄影师与模特搭建一个被更多人看见的展示窗口。
            </p>
            <div className="mt-6 space-y-3">
              {[
                { icon: "ri-mail-line", label: "合作邮箱", value: "contact@guomobailu.com" },
                { icon: "ri-wechat-line", label: "微信公众号", value: "国模白露" },
                { icon: "ri-map-pin-2-line", label: "工作地点", value: "上海 · 杭州" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3 rounded-md border border-background-200 bg-background-50 px-4 py-3">
                  <i className={`${item.icon} text-lg text-primary-600`} />
                  <div>
                    <p className="text-xs text-foreground-400">{item.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
              <i className="ri-chat-smile-2-line text-primary-500" />
              联系与反馈
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              合作邀约、作品授权或建议反馈，欢迎填写下方表单与我们联系。
            </p>

            {status === "success" ? (
              <div className="mt-6 flex flex-col items-center rounded-md bg-primary-50 px-4 py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-2xl text-background-50">
                  <i className="ri-check-line" />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">提交成功</h3>
                <p className="mt-2 text-sm text-foreground-600">
                  感谢你的留言，我们会尽快通过邮件与你联系。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                >
                  继续留言
                </button>
              </div>
            ) : (
              <form
                id="gmbl-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 space-y-4"
              >
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-sm text-foreground-700">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-sm text-foreground-700">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-1.5 block text-sm text-foreground-700">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的需求或建议（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <input
                  className="form-field-aux"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                {status === "error" && formError && (
                  <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-xs text-accent-900">
                    <i className="ri-error-warning-line" />
                    {formError}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  <i className="ri-send-plane-line" />
                  {status === "submitting" ? "提交中…" : "提交留言"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="mt-16 px-4 md:px-8">
        <h2 className="text-2xl font-bold text-foreground-950">常见问题</h2>
        <div className="mt-6 max-w-3xl divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {faqs.map((faq, index) => (
            <div key={faq.q}>
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
              >
                <span className="text-sm font-medium text-foreground-900">{faq.q}</span>
                <i
                  className={`${openFaq === index ? "ri-subtract-line" : "ri-add-line"} text-lg text-primary-600`}
                />
              </button>
              {openFaq === index && (
                <p className="px-5 pb-4 text-sm leading-relaxed text-foreground-600">{faq.a}</p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 rounded-lg bg-background-100 px-6 py-8 sm:flex-row md:px-10">
          <div>
            <h3 className="text-lg font-bold text-foreground-950">开始浏览更多写真作品</h3>
            <p className="mt-1 text-sm text-foreground-600">探索国模白露收录的全部模特写真摄影作品。</p>
          </div>
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-compass-3-line" />
            前往首页
          </Link>
        </div>
      </section>
    </div>
  );
}